import React, { useCallback, useMemo } from 'react';
import { gql } from '@apollo/react-hooks';
import type { ActionFn, Action, DataGridProps, SetIsLoading } from '#/common/components/DataGrid';
import DataGrid from '#/common/components/DataGrid';
import { getLocationList } from '#/graphql/queries';
import { Location } from '#/graphql/types';
import columns from './columns';
import { actions } from './actions';
import { UserRole } from '#/common/types/userTypes';
import useToast from '#/common/hooks/useToast';
import useSession from '#/common/hooks/useSession';
import useLocationDelete from '#/modules/asset/location/hooks/useDeleteLocationMutation';
import { inUseStatuses } from '../../utils/constants';

const getLocationListQuery = gql(getLocationList);

const userSelectedActionsPerRole: Record<UserRole, string[]> = {
  [UserRole.asset_manager]: ['delete-location'],
  [UserRole.trane_admin]: ['delete-location'],
  [UserRole.technician]: [],
};

const gridActionsPerRole: Record<UserRole, string[]> = {
  [UserRole.asset_manager]: ['new'],
  [UserRole.trane_admin]: ['new'],
  [UserRole.technician]: [],
};

const gridOptionsPerRole: Record<UserRole, Partial<DataGridProps<Location>>> = {
  [UserRole.asset_manager]: { selectionMode: 'multiple' },
  [UserRole.trane_admin]: { selectionMode: 'multiple' },
  [UserRole.technician]: { selectionMode: 'none' },
};

const LocationDataGrid: React.FC = () => {
  const { confirm } = useToast();
  const { user } = useSession();
  const { deleteLocation } = useLocationDelete();

  const role = user?.role as UserRole | undefined;

  const deleteItem = useCallback(
    async (ids: string[], { setIsLoading }: { setIsLoading?: SetIsLoading }) => {
      if (!ids) {
        return undefined;
      }
      const sure = await confirm('error', `Are you sure you want to delete these locations?`);
      if (!sure) {
        return;
      }
      setIsLoading && setIsLoading(true, 'Deleting data...');
      await deleteLocation(ids.join(','));
      setIsLoading && setIsLoading(false);
    },
    [deleteLocation],
  );

  const userSelectedActions: ActionFn<Location>[] = useMemo(() => {
    const res: ActionFn<Location>[] = [];

    if (role && userSelectedActionsPerRole[role]?.includes('delete-location')) {
      res.push({
        key: 'delete-location',
        icon: 'trash',
        text: 'Delete',
        disabled: (e) => {
          const filteredItemsForDelete = e.selectedItems.filter(
            (location) =>
              location.inUseStatus === inUseStatuses.newlyCreated.key &&
              !(location.thirdPartyId && location.source),
          );
          let count = filteredItemsForDelete?.length;
          return count < 1;
        },
        onClick: async (e) => {
          const filteredItemsForDelete = e.selectedItems.filter(
            (location) =>
              location.inUseStatus === inUseStatuses.newlyCreated.key &&
              !(location.thirdPartyId && location.source),
          );

          if (filteredItemsForDelete.length < e.selectedItems.length) {
            const sure = await confirm(
              'error',
              `Some selected locations are not in 'Newly Created' status and can not be deleted.`,
            );
            if (!sure) {
              return;
            }
          }

          await deleteItem(
            filteredItemsForDelete.map((location) => location.id!),
            { setIsLoading: e.setIsLoading },
          );
          e.dg?.instance.clearSelection();
          e.dg?.instance.refresh();
        },
      });
    }
    return res;
  }, [role]);

  const gridActions = useMemo<Action<Location>[]>(() => {
    return actions
      .filter((a) => typeof a !== 'object' || (role && gridActionsPerRole[role]?.includes(a?.key)))
      .map((a) => {
        if (typeof a === 'string') {
          return a;
        }
        const result = { ...a };
        return result;
      })
      .filter((a) => !!a);
  }, [role]);

  if (!role) return null;

  return (
    <DataGrid
      id="locations"
      pluralText="Locations"
      singularText="Location"
      columns={columns}
      recordUrl="/assets/locations/:id"
      actions={gridActions}
      selectedActions={userSelectedActions}
      getItemsQuery={getLocationListQuery}
      {...gridOptionsPerRole[role]}
    />
  );
};

export default React.memo(LocationDataGrid);
